import React, { useState, useEffect } from "react";
import { trackEvent, trackEcommerceEvent, formatPrice } from "../utils";

export function ProductCardInfo({ 
  product, 
  titleClass = "text-sm font-semibold tracking-tight uppercase whitespace-nowrap",
  notesClass = "text-xs text-neutral-500 font-light tracking-wide truncate",
  ratingColor = "text-neutral-200",
  ratingStarColor = "text-[#DFB37A]",
  ratingCountColor = "text-neutral-500",
  originalPriceClass = "text-[13px] line-through text-neutral-500",
  showInventory = false,
  className = ""
}: { 
  product: any, 
  titleClass?: string,
  notesClass?: string,
  ratingColor?: string,
  ratingStarColor?: string,
  ratingCountColor?: string,
  originalPriceClass?: string,
  showInventory?: boolean,
  className?: string
}) {
  const [timeLeft, setTimeLeft] = useState<{days: number, hours: number, minutes: number, seconds: number} | null>(null);
  const [isDiscountActive, setIsDiscountActive] = useState(false);

  useEffect(() => {
    if (!product?.discountValidity || !product?.discountPrice) {
      setIsDiscountActive(false);
      setTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      const validityDate = new Date(product.discountValidity).getTime();
      const now = new Date().getTime();
      const difference = validityDate - now;

      if (difference > 0) {
        setIsDiscountActive(true);
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      } else {
        setIsDiscountActive(false);
        setTimeLeft(null);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [product?.discountValidity, product?.discountPrice]);

  const discountPrice = isDiscountActive && product?.discountPrice ? parseInt(String(product.discountPrice).replace(/[^0-9]/g, ""), 10) : null;
  const originalPriceStr = formatPrice(product?.price || 45000);

  const hasReviews = !!(product.ratingCount > 0 && product.ratingAvg);
  const hasDiscount = !!discountPrice;
  const hasRow2Content = hasReviews || hasDiscount;

  const isPriceBlurred = product.accessMode === "waitlist" && !product.showPdp;

  return (
    <div className={`grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1.5 items-center ${className}`}>
      {/* Row 1: Name & Active Price */}
      <h3 className={`${titleClass} leading-none`} title={product.name}>
        {product.name}
      </h3>
      <div className="flex items-center justify-end whitespace-nowrap">
        {discountPrice ? (
          <span className={`${titleClass} !text-[#DFB37A] leading-none`}>
            {isPriceBlurred ? (
              <>
                <span>₹</span>
                <span className="blur-[4px] select-none inline-block ml-[1px]">{discountPrice.toLocaleString("en-IN")}</span>
              </>
            ) : (
              `₹${discountPrice.toLocaleString("en-IN")}`
            )}
          </span>
        ) : (
          <span className={`${titleClass} leading-none`}>
            {isPriceBlurred ? (
              <>
                <span>₹</span>
                <span className="blur-[4px] select-none inline-block ml-[1px]">{originalPriceStr.replace("₹", "")}</span>
              </>
            ) : (
              originalPriceStr
            )}
          </span>
        )}
      </div>

      {/* Row 2: Ratings & Original Price (strikethrough) */}
      {hasRow2Content && (
        <>
          <div className="flex items-center gap-1 min-h-[18px]">
            {hasReviews ? (
              <>
                <span className={`text-[13px] leading-none font-semibold ${ratingColor}`}>{product.ratingAvg}</span>
                <div className={`flex items-center -ml-0.5 ${ratingStarColor}`}>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <svg key={idx} className={`w-3 h-3 ${idx < Math.round(Number(product.ratingAvg)) ? 'fill-current' : 'text-white/20 fill-current'}`} viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className={`text-[11px] leading-none font-sans tracking-wide ${ratingCountColor}`}>({product.ratingCount})</span>
              </>
            ) : null}
          </div>
          <div className="flex items-center justify-end whitespace-nowrap min-h-[18px]">
            {hasDiscount && (
              <span className={`${originalPriceClass} leading-none`}>
                {isPriceBlurred ? (
                  <>
                    <span>₹</span>
                    <span className="blur-[3px] select-none inline-block ml-[1px]">{originalPriceStr.replace("₹", "")}</span>
                  </>
                ) : (
                  originalPriceStr
                )}
              </span>
            )}
          </div>
        </>
      )}

      {/* Row 3: Notes & Ends in / Inventory */}
      <div className="flex items-center min-h-[16px] min-w-0">
        <p className={`${notesClass} leading-none w-full`}>
          {product.notes}
        </p>
      </div>
      <div className="flex items-center justify-end whitespace-nowrap min-h-[16px]">
        {product?.inviteOnly ? (
          <div className="flex items-center justify-end gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full shrink-0 bg-[#DFB37A]"></div>
            <p className="text-[10px] leading-none font-bold uppercase tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A]">
              Invite Only
            </p>
          </div>
        ) : discountPrice && timeLeft ? (
          <span className={`${notesClass} leading-none !text-[#DFB37A]`}>
            Ends in {String(timeLeft.days * 24 + timeLeft.hours).padStart(2, '0')}h {String(timeLeft.minutes).padStart(2, '0')}m {String(timeLeft.seconds).padStart(2, '0')}s
          </span>
        ) : showInventory && !discountPrice ? (
          product?.dropDate ? (
            <div className="flex items-center justify-end gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full shrink-0 bg-[#DFB37A]"></div>
              <p className="text-[10px] leading-none font-bold uppercase tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A]">
                {product.dropDate}
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${product.inventory > 7 ? "bg-green-500" : product.inventory > 4 ? "bg-orange-500" : "bg-red-500"}`}></div>
              <p className={`text-[10px] leading-none font-bold uppercase tracking-wider ${product.inventory > 7 ? "text-green-500" : product.inventory > 4 ? "text-orange-500" : "text-red-500"}`}>
                {product.inventory} Pcs
              </p>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
